import { ImportModalPageType } from "PFApp/components/import_modal/constants/import_modal_page";
import { useImportModalContext } from "PFApp/components/import_modal/contexts/import_modal_context";
import { Button } from "PFComponents/button";
import { ColumnDef, ColumnHelper, HeaderContext } from "PFComponents/table/table.types";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { BulkOperationsState } from "PFCore/services/bulk_operations";
import { Import, ImportBase, ImportState } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ImportHistoryStatePill } from "../import_history_state_pill";

export type ImportBaseWithState = ImportBase & { state: ImportState | BulkOperationsState };

export type ColumnsGeneratorConfig<T extends ImportBaseWithState> = {
  isDetailsEnabled?: (row: T) => boolean;
  displayFileErrorsColumn?: boolean;
};

export const useColumnsGenerator = <T extends ImportBaseWithState>({
  isDetailsEnabled = (row) =>
    row.state === BulkOperationsState.Completed || row.state === BulkOperationsState.PartiallyCompleted,
  displayFileErrorsColumn = true
}: ColumnsGeneratorConfig<T>) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "importFiles.history.table.headers"
  });
  const { formatDate } = useDateFormatter();
  const { setImportData, setCurrentPage } = useImportModalContext<T>();

  const header = useCallback(
    (header: HeaderContext<T, unknown>) => (
      <Typography variant="h5">{t(header.header.id as unknown as TemplateStringsArray)}</Typography>
    ),
    [t]
  );

  return useCallback(
    (columnHelper: ColumnHelper<T>): ColumnDef<T, any>[] => {
      const columns: ColumnDef<T, any>[] = [
        columnHelper.accessor((row) => row.createdAt, {
          id: "createdAt",
          header,
          cell: (info) =>
            info.getValue() && (
              <Button
                kind="text"
                onClick={() => {
                  setImportData(info.row.original);
                  setCurrentPage(ImportModalPageType.ImportDataTable);
                }}
                disabled={!isDetailsEnabled(info.row.original)}
              >
                {formatDate(info.getValue())}
              </Button>
            ),
          size: 150,
          enableSorting: true
        }),
        columnHelper.accessor((row) => row.csvAttachment.filename, {
          id: "fileName",
          header,
          cell: (info) => info.getValue(),
          size: 150,
          enableSorting: false
        }),
        columnHelper.accessor((row) => row.creator, {
          id: "createdBy",
          header,
          cell: (info) => info.getValue() && getProfileName(info.getValue()),
          size: 150,
          enableSorting: false
        }),
        columnHelper.accessor((row) => row.state, {
          id: "state",
          header,
          cell: (info) => info.getValue() && <ImportHistoryStatePill state={info.getValue()} />,
          size: 150,
          enableSorting: false
        })
      ];

      if (displayFileErrorsColumn) {
        columns.push(
          columnHelper.accessor((row) => (row as unknown as Import).fileErrors, {
            id: "errors",
            header,
            cell: (info) =>
              (info.getValue() || []).map((error) => (
                <Typography withMargin key={String(error)} variant="bodyRegular">
                  {String(error)}
                </Typography>
              )),
            size: 150,
            enableSorting: false
          })
        );
      }

      return columns;
    },
    [formatDate, header, isDetailsEnabled, displayFileErrorsColumn, setCurrentPage, setImportData]
  );
};
